/* eslint-disable @nx/enforce-module-boundaries */

import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatTabNav, MatTabLink, MatTabNavPanel } from '@angular/material/tabs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, Observable, of, startWith, switchMap } from 'rxjs';

import { NinetyTabsBarComponent } from '@ninety/platform/components';
import { extractValueFromStore } from '@ninety/platform/services';
import {
  selectShowIssuesTrash,
  FeatureFlagFacade,
  FeatureFlagKeys,
  selectCompany,
  selectIsNinetyOsCompany,
  LayoutsTodosEvents,
  selectLanguage,
} from '@ninety/platform/state';
import { TerraIconModule } from '@ninety/terra';
import { HeadlinesStateActions } from '@ninety/web/pages/headlines/_state/headlines/headlines-state.actions';
import { selectIsHeadlinesTrashcanView } from '@ninety/web/pages/headlines/_state/headlines/headlines-state.selectors';
import { TeamTodoSelectors } from '@ninety/web/pages/todos/_state/team/team-todo.selectors';

import { FiltersToolbarComponent } from '../../components/filters-toolbar/filters-toolbar.component';
import { TopToolbarComponent } from '../../components/top-toolbar/top-toolbar.component';

interface TabGroupModel {
  tool: string;
  tabs: TabRouteModel[];
}

interface TabRouteModel {
  route: string;
  label: string;
  show$?: Observable<boolean>;
  dataCy?: string;
}

const TOOLS_WITHOUT_HEADER_SHADOW = new Set(['insights', 'chart', 'assessments', 'meetings', 'data']);

/** The org chart's trash sub-route: chart/primary/trash and chart/:chartId/trash. */
const ORG_CHART_TRASH_URL = /^\/chart\/[^/]+\/trash(?:[/?#(]|$)/;

@Component({
  selector: 'ninety-main-header',
  templateUrl: './main-header.component.html',
  styleUrls: ['./main-header.component.scss', './main-header.terrafy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.main-header--shadow]': 'showHeaderShadow()',
  },
  standalone: true,
  imports: [
    LetDirective,
    MatTabNav,
    MatTabLink,
    MatTabNavPanel,
    TerraIconModule,
    FiltersToolbarComponent,
    TopToolbarComponent,
    NinetyTabsBarComponent,
  ],
})
export class MainHeaderComponent {
  private readonly _router = inject(Router);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _featureFlags = inject(FeatureFlagFacade);
  private readonly store = inject(Store);

  readonly isHeadlinesTrashcanView$ = this.store.select(selectIsHeadlinesTrashcanView);
  readonly isTodosTrashcanView$ = this.store.select(TeamTodoSelectors.selectShowTrash);
  readonly language$ = this.store.select(selectLanguage);

  @Input() inMeeting: boolean;
  @Input() inFeedback: boolean;
  @Input() isMobile: boolean;
  @Input() isConversation: boolean;

  private readonly company = extractValueFromStore(this.store, selectCompany);
  private readonly language = extractValueFromStore(this.store, selectLanguage);

  // Master list of all terrafy tab data
  private readonly _allTabs: TabGroupModel[] = [
    {
      tool: 'issues',
      tabs: [
        { route: 'short-term', label: this.language?.issue?.shortTerm, dataCy: 'issues_term-tab' },
        { route: 'long-term', label: this.language?.issue?.longTerm, dataCy: 'issues_term-tab' },
        {
          route: 'archive',
          label: 'Archive',
          dataCy: 'issues_archive-tab',
          show$: this._featureFlags.getFlag(FeatureFlagKeys.issuesArchiveTab),
        },
      ],
    },

    {
      tool: 'todos',
      tabs: [
        { route: 'team', label: this.language?.todo?.team, dataCy: 'todos-app_tab-link-Team' },
        { route: 'personal', label: this.language?.todo?.personal, dataCy: 'todos-app_tab-link-Private' },
      ],
    },
    {
      tool: 'vision',
      tabs: [
        {
          route: 'vision',
          label: this.language?.vto?.vision,
          dataCy: 'terrafy-vision-tab-label',
        },
        {
          route: 'goals',
          label: this.language?.vto?.traction,
          dataCy: 'terrafy-traction-tab-label',
        },
        {
          route: 'long-term',
          label: this.language?.issue?.longTerm + ' ' + this.language?.issue?.items,
          show$: this.store.select(selectIsNinetyOsCompany),
          dataCy: 'terrafy-lti-tab-label',
        },
        { route: 'swot', label: this.language?.vto?.swot, dataCy: 'terrafy-swot-tab-label' },
      ],
    },
    {
      tool: '1-on-1',
      tabs: [
        {
          route: 'discussions/people-analyzer',
          label: this.language?.feedback?.peopleAnalyzer,
          dataCy: 'conversations_link-people-analyzer',
          show$: this._featureFlags.getFlagOnceLoaded(FeatureFlagKeys.webPeopleAnalyzer),
        },
        {
          route: 'discussions/quarterly',
          label: this.language?.feedback?.quarterlyItems,
          dataCy: 'conversations_link-quarterly',
          show$: this._featureFlags.getFlag(FeatureFlagKeys.enableEosPure).pipe(
            map(enabled => {
              if (enabled) {
                return this.company?.settings?.quarterlyDiscussions === true;
              }
              return true;
            })
          ),
        },
        {
          route: 'discussions/annual',
          label: this.language?.feedback?.annualItems,
          dataCy: 'conversations_link-annual',
        },
        {
          route: 'discussions/fit-check',
          label: 'Fit Check',
          dataCy: 'conversations_link-fit-check',
          show$: this._featureFlags.getFlagOnceLoaded(FeatureFlagKeys.webPeopleAnalyzer).pipe(map(enabled => !enabled)),
        },
      ],
    },
  ];
  protected urlAfterRedirects$ = this._router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map((event: NavigationEnd) => {
      return event.urlAfterRedirects;
    }),
    startWith(this._router.url)
  );

  /** A signal so the host class binding tracks it directly rather than relying on the view being dirtied. */
  protected readonly showHeaderShadow = toSignal(
    this.urlAfterRedirects$.pipe(map(url => this._shouldShowHeaderShadow(url))),
    { initialValue: false }
  );

  protected activeTool$ = this.urlAfterRedirects$.pipe(
    switchMap(url => {
      return of(this._getActiveTool(url));
    })
  );

  protected activeRoute$ = combineLatest([this.activeTool$, this.urlAfterRedirects$]).pipe(
    switchMap(([activeTool, url]) => {
      return of(this._getActiveRoute(activeTool, url));
    })
  );

  // Stream of tabs for the active tool
  protected toolTabGroup$ = this.activeTool$.pipe(
    startWith(this._getActiveTool(this._router.url)),
    switchMap(activeTool => {
      return of(this._getTabGroupForTool(this._allTabs, activeTool));
    })
  );

  protected data$ = {
    activeRoute: this.activeRoute$,
    activeTool: this.activeTool$,
    toolTabGroup: this.toolTabGroup$,
    showIssuesTrash: this.store.select(selectShowIssuesTrash),
    weeklyConversationsFeatureFlag: this._featureFlags.getFlag(FeatureFlagKeys.webWeeklyConversations),
  };

  private _shouldShowHeaderShadow(url: string): boolean {
    // The org chart canvas hides the filters toolbar, which is why 'chart' is excluded. Its trash
    // sub-route brings the toolbar back, so the chart-wide exclusion must not apply there.
    if (ORG_CHART_TRASH_URL.test(url)) return true;

    return !TOOLS_WITHOUT_HEADER_SHADOW.has(this._getActiveTool(url));
  }

  private _getActiveRoute(activeTool: string, url: string): string {
    // Replace cleans urls that include other named router outlets like todos details pane
    const splitUrl = url.replace(/\([^]*/g, '').split('/');
    const rootTool = splitUrl[1];

    if (activeTool === '1-on-1') {
      return `${splitUrl[2]}/${splitUrl[3]}`;
    }

    if (activeTool === 'issues') {
      if (rootTool === 'meeting') {
        return splitUrl[splitUrl.length - 1];
      }
    }
    return splitUrl[2];
  }

  private _getActiveTool(url: string): string {
    const splitUrl = url.split('/');
    const rootTool = splitUrl[1];

    // Meeting has nested tools like vision and issues
    // if (rootTool === 'meeting') {
    //   const meetingTool = splitUrl[splitUrl.length - 1];
    //   switch (meetingTool) {
    //     case 'short-term':
    //     case 'long-term':
    //       // For issues nested under rock review we don't want to show new tabs as it wasn't working
    //       if (url.includes('/rocks')) {
    //         return rootTool;
    //       }
    //       return 'issues';
    //   }
    // }

    // Disables the tabs for the 1-on-1 tool when the form is open
    if (rootTool === '1-on-1' && splitUrl.at(-1) === 'form') {
      return '';
    }

    return rootTool;
  }

  private _getTabGroupForTool(tabsData: TabGroupModel[], tool: string): TabGroupModel {
    return tabsData.find(tab => tab.tool === tool);
  }

  navigateTo(activeTool: string, route: string): void {
    const inMeeting = this._activatedRoute.firstChild?.snapshot.url.some(segment => segment.path.includes('meeting'));

    if (inMeeting) {
      this._router.navigate([activeTool, route], { relativeTo: this._activatedRoute.firstChild });
    } else {
      this._router.navigate([`/${activeTool}/${route}`]);
    }
  }

  backToHeadlines(): void {
    this.store.dispatch(HeadlinesStateActions.setShowTrash({ show: false }));
    if (this._activatedRoute.snapshot.queryParamMap.get('view') === 'trash') {
      void this._router.navigate([], {
        relativeTo: this._activatedRoute,
        queryParams: { view: null },
        queryParamsHandling: 'merge',
      });
    }
  }

  backToTodos(): void {
    // Trash is an in-page state mode (no route query param), so we only reset the flag — the
    // next visit starts on the live list. Handled by the to-dos team state.
    this.store.dispatch(LayoutsTodosEvents.hideTrashItems());
  }
}
